import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

import { useQuery } from "@tanstack/react-query";
import type { SellerInsights, SellerRateCompare } from "@recruiter/shared";

import { formatUsd, plural } from "../../lib/format";
import { insightTips, pct, replyTime, standing } from "../../lib/insights";
import { insightsQueryOptions, SellerQueryError } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { SellerErrorState } from "../seller/seller-error-state";
import { BarsIcon, CloseIcon, MiraMarkIcon } from "./icons";

// The Pro insights view: how the talent does with the projects Mira brings them, set
// against the other talents on those same projects. Every number comes from
// `GET /api/seller/insights` (the backend derives them; other talents only ever appear
// pooled); this view phrases and draws them. A modal over the page, read when it opens.
export function InsightsPanel() {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const setInsightsOpen = useSellerStore((s) => s.setInsightsOpen);
  const setPrefsOpen = useSellerStore((s) => s.setPrefsOpen);
  const q = useQuery(insightsQueryOptions(sellerId));
  const panelRef = useRef<HTMLDivElement | null>(null);
  const close = () => setInsightsOpen(false);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  }

  return (
    <div className="sl-insights" role="presentation">
      <button
        type="button"
        className="sl-drawer__scrim"
        aria-label="Close insights"
        tabIndex={-1}
        onClick={close}
      />
      <div
        ref={panelRef}
        className="sl-insights__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sl-insights-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="sl-insights__head">
          <span className="sl-drawer__badge" aria-hidden="true">
            <BarsIcon size={18} />
          </span>
          <div className="sl-drawer__heading">
            <h2 id="sl-insights-title" className="sl-drawer__title">
              Insights <span className="sl-pro-badge">Pro</span>
            </h2>
            <p className="sl-drawer__lede">
              {q.data && q.data.field_talents > 0
                ? `How you do with the projects Mira brings you, next to the ${plural(q.data.field_talents, "other talent")} on the same projects.`
                : "How you do with the projects Mira brings you."}
            </p>
          </div>
          <button type="button" className="sl-icon-btn" onClick={close} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </header>
        <div className="sl-insights__body">
          {q.isPending ? (
            <div className="sl-insights__grid" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="sl-skel sl-skel--card" />
              ))}
            </div>
          ) : q.isError ? (
            <SellerErrorState
              title="Couldn't load your insights"
              message={
                q.error instanceof SellerQueryError ? q.error.message : "Something went wrong."
              }
              onRetry={() => void q.refetch()}
            />
          ) : q.data.projects === 0 ? (
            <p className="sl-insights__empty">
              Your insights start with your first project. When Mira reaches out, your numbers show
              up here.
            </p>
          ) : (
            <InsightsBody
              i={q.data}
              onSetMinimums={() => {
                close();
                setPrefsOpen(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function InsightsBody({ i, onSetMinimums }: { i: SellerInsights; onSetMinimums: () => void }) {
  const tips = insightTips(i);
  const won = i.outcomes;
  const settled = won.won + won.not_picked + won.no_proposal + won.no_reply + won.declined;
  return (
    <>
      <dl className="sl-insights__grid">
        <Kpi
          label="Win rate"
          value={pct(i.win_rate.you)}
          note={versus(i.win_rate)}
          tone={toneFor(i.win_rate)}
        />
        <Kpi
          label="Reply rate"
          value={pct(i.reply_rate.you)}
          note={versus(i.reply_rate)}
          tone={toneFor(i.reply_rate)}
        />
        <Kpi
          label="Typical reply time"
          value={replyTime(i.reply_minutes)}
          note={
            i.field_reply_minutes !== null
              ? `Others: ${replyTime(i.field_reply_minutes)}`
              : "No other replies yet"
          }
          tone={
            i.reply_minutes !== null &&
            i.field_reply_minutes !== null &&
            i.reply_minutes <= i.field_reply_minutes
              ? "ok"
              : "ai"
          }
        />
        <Kpi
          label="In play"
          value={formatUsd(i.pipeline_usd)}
          note={i.won_usd > 0 ? `${formatUsd(i.won_usd)} won so far` : "Your open proposals"}
          tone="ai"
        />
      </dl>

      {tips.length > 0 ? (
        <section className="sl-insights__section" aria-labelledby="sl-tips-title">
          <h3 id="sl-tips-title" className="sl-insights__h">
            <MiraMarkIcon size={18} /> What Mira would tell you
          </h3>
          <ul className="sl-tips">
            {tips.map((t) => (
              <li key={t.title} className={`sl-tip sl-tone--${t.tone}`}>
                <span className="sl-tip__title">{t.title}</span>
                <span className="sl-tip__body">{t.body}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="sl-insights__cols">
        <Card
          title="You against the field"
          sub="Your share, next to the other talents on the same projects"
        >
          <Versus label="Replied" rate={i.reply_rate} />
          <Versus label="Sent a proposal" rate={i.proposal_rate} />
          <Versus label="Won, of proposals" rate={i.win_rate} />
          <p className="sl-insights__foot">
            {i.first_to_reply > 0
              ? `You were the first to reply on ${i.first_to_reply} of ${plural(i.replied, "project")}.`
              : `${plural(i.projects, "project")} so far.`}
          </p>
        </Card>

        <Card title="Your pricing" sub="Your latest quote on each project">
          <Stat
            label="Against the client's budget"
            value={signedPct(i.quote_vs_budget_pct)}
            note={
              i.quote_vs_budget_base > 0
                ? `Typical gap, across ${plural(i.quote_vs_budget_base, "project")}`
                : "No quote on a project with a stated budget yet"
            }
          />
          <Stat
            label="Against the typical quote"
            value={signedPct(i.quote_vs_field_pct)}
            note={
              i.quote_vs_field_base > 0
                ? `Across ${plural(i.quote_vs_field_base, "project")} with 3 or more other quotes`
                : "Shown once 3 or more other talents quote on a project you quoted on"
            }
          />
          {i.minimums_set ? (
            <Stat
              label="Under your minimums"
              value={String(i.below_minimum)}
              note={`${plural(i.fits_minimum, "project")} met them`}
            />
          ) : (
            <div className="sl-insights__stat">
              <span className="sl-insights__stat-label">Your minimums</span>
              <button type="button" className="sl-link" onClick={onSetMinimums}>
                Set your minimum rates
              </button>
            </div>
          )}
        </Card>

        <Card title="What the projects pay" sub="The work Mira brings you">
          <Stat
            label="Typical budget"
            value={i.budget_median_usd !== null ? formatUsd(i.budget_median_usd) : "-"}
            note="Fixed-price projects"
          />
          <Bars items={i.budget_kinds} total={i.projects} />
          {i.industries.length > 0 ? (
            <>
              <span className="sl-insights__stat-label">Who hires you</span>
              <div className="sl-insights__chips">
                {i.industries.map((x) => (
                  <span key={x.label} className="sl-status sl-status--chip sl-tone--slate">
                    {x.label} · {x.count}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </Card>

        <Card
          title="How projects ended"
          sub={settled > 0 ? plural(settled, "project") : "None ended yet"}
        >
          <Bars
            items={[
              { label: "Won", count: won.won },
              { label: "Proposed, not picked", count: won.not_picked },
              { label: "Replied, no proposal", count: won.no_proposal },
              { label: "No reply", count: won.no_reply },
              { label: "Not moving forward", count: won.declined },
            ].filter((x) => x.count > 0)}
            total={settled}
          />
        </Card>
      </div>

      <section className="sl-insights__section sl-soon" aria-labelledby="sl-soon-title">
        <h3 id="sl-soon-title" className="sl-insights__h">
          Coming to Pro
        </h3>
        <ul className="sl-soon__list">
          <li>The price range of other proposals on each project</li>
          <li>Coaching on every proposal: what made the client pass</li>
          <li>Market rates for your kind of work, from Fiverr</li>
          <li>A weekly report in your inbox</li>
        </ul>
      </section>
    </>
  );
}

function versus(rate: SellerRateCompare): string {
  return rate.field === null ? "No others to compare yet" : `Others: ${pct(rate.field)}`;
}

function toneFor(rate: SellerRateCompare): "ok" | "wait" | "ai" {
  const s = standing(rate);
  return s === "ahead" ? "ok" : s === "behind" ? "wait" : "ai";
}

function signedPct(value: number | null): string {
  if (value === null) return "-";
  if (value === 0) return "On par";
  return value > 0 ? `${value}% above` : `${-value}% below`;
}

function Kpi({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone: "ok" | "wait" | "ai";
}) {
  return (
    <div className={`sl-stat sl-tone--${tone}`}>
      <dt className="sl-stat__label">{label}</dt>
      <dd className="sl-stat__value">{value}</dd>
      <dd className="sl-stat__note">{note}</dd>
    </div>
  );
}

function Card({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <section className="sl-icard">
      <h3 className="sl-icard__title">{title}</h3>
      <p className="sl-icard__sub">{sub}</p>
      <div className="sl-icard__body">{children}</div>
    </section>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="sl-insights__stat">
      <span className="sl-insights__stat-label">{label}</span>
      <span className="sl-insights__stat-value">{value}</span>
      <span className="sl-insights__stat-note">{note}</span>
    </div>
  );
}

/** Your share and the field's, as two bars on one 0-100% scale. */
function Versus({ label, rate }: { label: string; rate: SellerRateCompare }) {
  return (
    <div className="sl-versus">
      <span className="sl-versus__label">{label}</span>
      <span className="sl-versus__row">
        <span className="sl-versus__who">You</span>
        <span className="sl-versus__track" aria-hidden="true">
          <span className="sl-versus__fill" style={{ width: `${(rate.you ?? 0) * 100}%` }} />
        </span>
        <span className="sl-versus__v">{pct(rate.you)}</span>
      </span>
      <span className="sl-versus__row sl-versus__row--field">
        <span className="sl-versus__who">Others</span>
        <span className="sl-versus__track" aria-hidden="true">
          <span className="sl-versus__fill" style={{ width: `${(rate.field ?? 0) * 100}%` }} />
        </span>
        <span className="sl-versus__v">{pct(rate.field)}</span>
      </span>
    </div>
  );
}

/** Labelled counts as bars of a shared total. */
function Bars({ items, total }: { items: Array<{ label: string; count: number }>; total: number }) {
  if (items.length === 0) return null;
  return (
    <ul className="sl-ibars">
      {items.map((x) => (
        <li key={x.label} className="sl-ibars__row">
          <span className="sl-ibars__label">{x.label}</span>
          <span className="sl-ibars__track" aria-hidden="true">
            <span
              className="sl-ibars__fill"
              style={{ width: `${total > 0 ? (x.count / total) * 100 : 0}%` }}
            />
          </span>
          <span className="sl-ibars__v">{x.count}</span>
        </li>
      ))}
    </ul>
  );
}
